<!--
Modal letting an editor search the USDA FoodData Central database directly and
link a chosen entry to ONE specific ingredient, pulling its nutrition facts.
Usable from any ingredient's own page — an editor can link an ingredient that
has no nutrition data yet, or correct an existing match.

<UsdaLinkModal
    :name="ingredientName"
    :english-name-used="ingredient.english_name"
    v-on:linked="({ nutrition, new_units }) => ..."
    v-on:decline-action="show... = false"
/>
Closes on the × button, or on a click outside the modal card.
-->
<template>
    <div class="popup" @click.self="$emit('decline-action')">
        <div class="usda_link_modal">
            <button type="button" class="close_btn" @click="$emit('decline-action')" :aria-label="$t('meal_plans.back')">×</button>
            <h4>{{ $t('nutrition.usda_link_title') }}</h4>
            <p class="ingredient_name">{{ name }}</p>

            <div class="search_row">
                <input
                    type="text"
                    class="text_input"
                    v-model="query"
                    :placeholder="$t('nutrition_status.usda_search_placeholder')"
                    @keyup.enter="search"
                />
                <button type="button" class="primary_btn small_btn" :disabled="!query.trim() || searching" @click="search">{{ $t('nutrition_status.search') }}</button>
            </div>

            <span class="error_text" v-if="error">{{ error }}</span>
            <ul class="usda_results" v-if="results.length > 0">
                <li v-for="r in results" :key="r.fdc_id">
                    <div class="result_main">
                        <span class="result_description">{{ r.description }} <span class="data_type">({{ r.data_type }})</span></span>
                        <button type="button" class="link_btn" :disabled="linking" @click="link(r.fdc_id)">{{ $t('nutrition_status.link') }}</button>
                    </div>
                    <p class="result_macros" v-if="r.per_100">
                        {{ r.per_100.calories }} kcal · {{ $t('nutrition.protein') }} {{ r.per_100.protein }}g · {{ $t('nutrition.carbohydrates') }} {{ r.per_100.carbohydrates }}g · {{ $t('nutrition.fats') }} {{ r.per_100.fats }}g
                        <span class="per_100_hint">({{ $t('nutrition.per_100_basis') }} g)</span>
                    </p>
                </li>
            </ul>
            <p class="empty_text" v-else-if="searched && results.length === 0">{{ $t('nutrition_status.no_results') }}</p>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
    name: "UsdaLinkModal",
    props: ['name', 'englishNameUsed'],
    data() {
        return {
            query: this.englishNameUsed || '',
            results: [],
            searching: false,
            searched: false,
            linking: false,
            error: null
        }
    },
    computed: {
        ...mapGetters({
            user: "user"
        })
    },
    methods: {
        describeError(error) {
            if (error === 'usda_rate_limited') { return this.$t('nutrition_status.usda_rate_limited'); }
            return typeof error === "string" ? error : JSON.stringify(error);
        },
        search() {
            if (!this.query.trim()) { return; }
            this.searching = true;
            this.error = null;
            this.searched = false;
            fetch(process.env.VUE_APP_API + `/ingredients/usda_search?q=` + encodeURIComponent(this.query.trim()), {
                headers: {
                    "TOKEN": JSON.stringify(this.user.data.token)
                }
            }).then(res => res.json())
              .then(res => {
                  this.searching = false;
                  this.searched = true;
                  if (res.error) {
                      this.error = this.describeError(res.error);
                  } else {
                      this.results = res.results;
                  }
              });
        },
        link(fdc_id) {
            this.linking = true;
            this.error = null;
            fetch(process.env.VUE_APP_API + `/ingredients/` + encodeURIComponent(this.name) + `/usda_match`, {
                method: "post",
                headers: {
                    "TOKEN": JSON.stringify(this.user.data.token),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ fdc_id: fdc_id })
            }).then(res => res.json())
              .then(res => {
                  this.linking = false;
                  if (res.error) {
                      this.error = this.describeError(res.error);
                  } else {
                      // Pass both back rather than making the parent re-fetch the
                      // ingredient — getByName reads an eventually-consistent
                      // cache that can still be stale immediately after this
                      // write commits, but this response IS the authoritative
                      // fresh result.
                      this.$emit('linked', { nutrition: res.nutrition, new_units: res.new_units || [] });
                  }
              });
        }
    }
}
</script>

<style scoped>
.popup {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: rgba(0, 0, 0, 0.6);
}
.usda_link_modal {
    position: relative;
    max-width: 560px;
    min-width: 260px;
    top: 12%;
    margin: 1rem auto;
    padding: 2rem;
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 0 70px #000000a6;
    text-align: left;
    max-height: 74vh;
    overflow-y: auto;
}
.dark .usda_link_modal {
    background-color: var(--color-bg-primary-dark);
    color: var(--color-text-primary-dark);
}
.close_btn {
    position: absolute;
    top: 0.6rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.6rem;
    line-height: 1;
    cursor: pointer;
    color: var(--color-grey-2);
}
.ingredient_name {
    font-weight: 600;
    margin-bottom: 1rem;
}
.search_row {
    display: flex;
    gap: 0.6rem;
    align-items: center;
}
.search_row .text_input {
    flex: 1;
}
.small_btn {
    padding: 0.4rem 1rem;
    white-space: nowrap;
}
.usda_results {
    list-style: none;
    padding: 0;
    margin-top: 1rem;
}
.usda_results li {
    padding: 0.5rem;
    border-bottom: var(--line-dashed);
}
.usda_results li:hover {
    background-color: var(--color-bg-primary-dark, #f5f5f5);
}
.result_main {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.6rem;
}
.data_type {
    color: var(--color-grey-2);
    font-size: 0.8rem;
}
.result_macros {
    margin: 0.3rem 0 0;
    font-size: 0.8rem;
    color: var(--color-grey-2);
}
.per_100_hint {
    font-style: italic;
}
.link_btn {
    background: none;
    border: 1px solid var(--color-secondary);
    color: var(--color-secondary);
    border-radius: 50px;
    padding: 0.2rem 1rem;
    font-size: 0.85rem;
    cursor: pointer;
    white-space: nowrap;
}
.error_text {
    color: var(--color-red, #c0392b);
    font-size: 0.85rem;
    display: block;
    margin-top: 0.6rem;
}
.empty_text {
    color: var(--color-grey-2);
    font-size: 0.9rem;
    margin-top: 0.6rem;
}
</style>
